import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import { AlertTriangle, CheckCircle2, Rocket, Menu } from 'lucide-react';

const SubscriptionExpired: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Color tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#94A3B8' : '#64748B';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const featureText = isDark ? '#CBD5E1' : '#475569';
  const checkColor = isDark ? '#4ADE80' : '#22C55E';
  const noticeColor = isDark ? '#64748B' : '#94A3B8';

  const features = [
    t('subscriptions.expiredFeatures.unlimitedAccess'),
    t('subscriptions.expiredFeatures.prioritySupport'),
    t('subscriptions.expiredFeatures.cancelAnytime'),
    t('subscriptions.expiredFeatures.exclusiveContent'),
  ];

  return (
    <div
      className="flex flex-col items-center justify-center flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{ background: containerBg }}
    >
      <div
        className="rounded-none lg:rounded-2xl"
        style={{
          background: mainBg,
          boxShadow: mainShadow,
          maxWidth: '480px',
          width: '100%',
          padding: '48px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Hamburger menu (mobile) */}
        <button
          className="lg:hidden flex items-center justify-center flex-shrink-0 self-start"
          style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, marginBottom: '8px' }}
          onClick={toggleSidebar}
        >
          <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
        </button>

        {/* Warning Icon */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: isDark ? 'rgba(239, 68, 68, 0.1)' : '#FEF2F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <AlertTriangle size={28} color={isDark ? '#F87171' : '#EF4444'} />
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: '20px',
            fontWeight: 700,
            color: titleColor,
            marginBottom: '8px',
            textAlign: 'center',
          }}
        >
          {t('subscriptions.subscriptionExpired')}
        </h2>

        {/* Description */}
        <p
          style={{
            fontSize: '13px',
            color: subtitleColor,
            textAlign: 'center',
            lineHeight: '1.6',
            marginBottom: '28px',
            maxWidth: '360px',
          }}
        >
          {t('subscriptions.renewMessage')}
        </p>

        {/* Features Card */}
        <div
          style={{
            width: '100%',
            background: cardBg,
            borderRadius: '12px',
            border: `1px solid ${cardBorder}`,
            padding: '16px 20px',
            marginBottom: '28px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {features.map((feature, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <CheckCircle2 size={16} color={checkColor} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '13px', color: featureText, fontWeight: 500 }}>
                {feature}
              </span>
            </div>
          ))}
        </div>

        {/* Upgrade Button */}
        <button
          onClick={() => navigate('/subscriptions')}
          style={{
            width: '100%',
            padding: '12px',
            background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
            color: '#FFFFFF',
            borderRadius: '10px',
            border: 'none',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '16px',
          }}
        >
          <Rocket size={16} />
          {t('subscriptions.upgradeNow')}
        </button>

        {/* Notice */}
        <p
          style={{
            fontSize: '12px',
            color: noticeColor,
            textAlign: 'center',
            lineHeight: '1.5',
            margin: 0,
          }}
        >
          {t('subscriptions.accessRestoredMessage')}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionExpired;
